import React, { useEffect, useState } from "react";
import { useSelector } from "../../../store";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import User from "../../../types/user";
import AddEditUserForm from "../../forms/AddEditUserForm";
import SettingsModal from "../../../types/settingsModal";
import { Base } from "../../base";
import ColumnDef from "../../../types/columnDef";
import { Box, Grid, Tooltip } from "@mui/material";
import { Work } from "@mui/icons-material";
import checkRole, { UserRoles } from "../../../helpers/checkRole";

interface Props {
  getUsers: () => void;
  apiGetUser: (id: number) => void;
  resetUser: () => void;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  setUserFilter: (params: SearchParams) => void;
}

const Users: React.FC<Props> = (props) => {
  const { getUsers, apiGetUser, resetUser, phone, setUserFilter } = props;
  const { users, status } = useSelector((store) => store.user);
  const { loggedInUser } = useSelector((store) => store.authentication);
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [addEditModal, setAddEditModal] = useState(false);
  const [highlightId, setHighlightId] = useState(0);
  const [editUser, setEditUser] = useState<User | undefined>(undefined);

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phone) {
      setMobileRows(users);
    }
  }, [users, phone]);

  const columns: ColumnDef[] = [
    {
      field: "firstName",
      headerName: "First Name",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "lastName",
      headerName: "Last Name",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "email",
      headerName: "Email",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "assigned",
      headerName: "Assigned Job Types",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((user: User) => {
      let rowObject = {
        id: user.gmdId,
        mobileTitle: `${user.firstName} ${user.lastName}`,
        firstName: `${user.firstName}`,
        lastName: `${user.lastName}`,
        email: `${user.email}`,
        assigned: user.assignedJobTypes?.length ? (
          <Grid
            container
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Grid>{user.assignedJobTypes.length ?? 0}</Grid>
            <Tooltip
              title={
                <Box>
                  {user.assignedJobTypes.map((a) => (
                    <Box key={a.id}>
                      <Base.Text text={`${a.name}`} />
                    </Box>
                  ))}
                </Box>
              }
            >
              <Work />
            </Tooltip>
          </Grid>
        ) : (
          "None"
        ),
        // role: user.role,
        actions: (
          <Base.TableActions
            id={user.gmdId!}
            handleEdit={async () => {
              apiGetUser(user.gmdId!);
              setAddEditModal(true);
              setHighlightId(user.gmdId!);
              setEditUser(user);
            }}
            handleAdminRoute={
              checkRole(loggedInUser!, UserRoles.GlobalAdmin)
                ? () => handleAdminRoute(user.id)
                : undefined
            }
          />
        ),
      };
      return rowObject;
    });
  };

  const close = () => {
    // apiUserSearch(defaultParams);
    setUserFilter(defaultParams);
    setAddEditModal(false);
    resetUser();
    setHighlightId(0);
    setEditUser(undefined);
  };

  const handleSaveUpdate = () => {
    getUsers();
  };

  const handleAdminRoute = (id?: number) => {
    window.location.href = `${process.env.REACT_APP_AUTH_URL}/app/users/${id}`;
  };

  return (
    <>
      <Box pb={{ xs: 15, md: 5 }}>
        <Base.TableContainer
          columns={columns}
          rows={displayRows(users)}
          mobileRows={displayRows(mobileRows)}
          page={1}
          totalRows={users.length}
          loading={status === "loading"}
          highlightId={highlightId}
        />
      </Box>
      <Base.Modal open={addEditModal} onClose={() => {}}>
        <AddEditUserForm
          close={close}
          onSave={handleSaveUpdate}
          user={editUser}
        />
      </Base.Modal>
    </>
  );
};

export default Users;
